import { AxiosResponse } from 'axios';
import {
  checkActiveShift,
  ShiftActionType,
  View,
} from 'Contexts/CalendarContext';
import { RestaurantContext, Severity } from 'Contexts/RestaurantContext';
import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
  ShiftType,
  Date as DateType,
  ReservationShift,
  GuestOrderingShift,
  TakeAwayShift,
  Shift,
  AnyShift,
} from 'types/shifts';
import { db } from 'utils/firebase';
import { dateHelper, getCalendarWeek, timeToBlock } from 'utils/helper';
import server from 'utils/server';
import { getCalendarWeekArray } from './useCalendar';
import {
  NewGuestOrderingShift,
  NewHappyHoursShift,
  NewReservationShift,
} from 'utils/ShiftClasses';
import useAuth from './useAuth';
import useDateString from './useDateString';
import useLanguage from './useLanguage';
import TEMPLATES from 'utils/calendarTemplates';
import { CalendarShift } from 'App/Calendar/Components/Week';
import useRecentShifts from './useRecentShifts';
import { triggerCustomEvent } from './useCustomEvent';
import { cloneEventWithNewId } from 'utils/normalizeEventId';
import { dayOverrideChanges, DayOverrideChanges } from 'utils/dayOverrides';
import updateShiftOnDay from 'utils/updateShiftOnDay';
import { SecurityGrant, trackChange } from 'utils/securityActivity/grant';

function getDateArray(dateStr: string, n: number) {
  let dateArray: string[] = [];

  for (let i = 0; i < n; i++) {
    let date = new Date(dateStr);
    date.setDate(date.getDate() + i);
    dateArray.push(date.toISOString().split('T')[0]);
  }
  return dateArray;
}

type ShiftActionBody = {
  date: string;
  allFutureDates?: boolean;
  type?: ShiftActionType;
  loadDates?: boolean;
  data?: AnyShift;
  dayOverrideChanges?: DayOverrideChanges;
  disableOnly?: boolean;
};

const COLORS = ['#10CCB9', '#6369D1', '#EE4266', '#1E90FF', '#CCC910'];

function cloneShiftEventWithNewId<T extends Partial<ReservationShift>>(
  shift: T
): T {
  const eventV02 = shift.additional?.eventV02;
  if (!eventV02) return shift;

  return {
    ...shift,
    additional: {
      ...shift.additional,
      eventV02: cloneEventWithNewId(eventV02),
    },
  };
}

export type ActiveShift<T extends Shift = Shift> = {
  shift: Partial<T>;
  date: string;
  type: 'new' | 'edit' | 'duplication' | 'close';
  allUpcomingShifts?: boolean;
  eventOnly?: boolean;
  blockBefore?: boolean;
  /** Security Plus grants that opened or changed this editor; UI state only, never saved. */
  grants?: SecurityGrant[];
} | null;

/** Ids a shift carries before it is saved (new shift, copy). */
const PLACEHOLDER_SHIFT_IDS = ['new-shift', 'newId'];

/** A create answers with the id the server gave the new shift. */
type ShiftSaveAnswer = { success: boolean; shiftId?: string };

const useCalendarDates = <TShift extends Shift = AnyShift>(
  restaurantId: string,
  type: ShiftType,
  date: string,
  view: View,
  allOccassions: { id: string; title: string }[] = [],
  setDate: (date: string) => void
) => {
  const [currentMultipleShifts, setCurrentMultipleShifts] = useState<
    CalendarShift[] | null
  >(null);

  const { newToast, alert, products } = useContext(RestaurantContext);
  const { uid } = useAuth();
  const { language } = useLanguage();

  const [selectedMultiCard, setSelectedMultiCard] = useState<string | null>(
    null
  );

  const lightReservations = products.includes('light-reservation');

  const [dates, setdates] = useState<DateType<TShift>[]>([]);
  const [calendarWeek, setcalendarWeek] = useState<number | null>(null);
  const [loading, setloading] = useState(false);
  const [error, seterror] = useState<string | null>(null);

  const [offset, setoffset] = useState(0);

  const [currentShift, setcurrentShift] = useState<null | {
    date: string;
    shiftId: string;
  }>(null);

  const [activeShift, setactiveShift] = useState<ActiveShift<TShift>>(null);

  // used when user asked whether he wants to edit only current shift
  // or all upcoming shifts with same id

  const loadWeek = (
    newCalendarWeek: number = calendarWeek || getCalendarWeek(new Date(date))
  ) => {
    console.log('load week');
    setloading(true);
    setcalendarWeek(newCalendarWeek);

    let serverCall: Promise<
      AxiosResponse<{ dates: DateType<TShift>[]; offset?: number }>
    >;

    let weekId =
      newCalendarWeek >= 0
        ? date.slice(0, 4) + '-' + newCalendarWeek
        : Number(date.slice(0, 4)) -
          1 +
          '-' +
          getCalendarWeek(new Date(`${Number(date.slice(0, 4)) - 1}-12-31`));

    if (view === View.WEEK || view === View.LIST) {
      serverCall = server.get<{ dates: DateType<TShift>[] }>(
        `/v03/shifts/${restaurantId}/weeks/${weekId}/${type}`
      );
    } else if (view === View.LIST) {
      serverCall = server.post<{ dates: DateType<TShift>[]; offset: number }>(
        `/v03/${restaurantId}/shifts/dates/${type}`,
        { date, view, offset }
      );
    } else {
      serverCall = server.post<{ dates: DateType<TShift>[] }>(
        `/v03/${restaurantId}/shifts/dates/${type}`,
        { date, view }
      );
    }

    serverCall
      .then(({ data }) => {
        const { dates, offset } = data;

        if (offset) {
          setdates((d) => [...d, ...dates]);
        } else {
          setdates(dates);
          if (
            currentShift &&
            !dates
              .find((d) => d.date === currentShift.date)
              ?.shifts?.find((s) => s.id === currentShift.shiftId)
          ) {
            setcurrentShift(null);
          }
        }

        seterror(null);
      })
      .catch((err) => {
        let dates: DateType<TShift>[] = getCalendarWeekArray(
          newCalendarWeek,
          parseInt(date.slice(0, 4))
        ).map((date) => ({
          date,
          shifts: [],
          wDay: new Date(date).getDay(),
          holiday: null,
        }));

        setdates(dates);
        seterror(err?.response?.data?.message || err.message);
      })
      .finally(() => {
        setloading(false);
      });
  };

  const [oldType, setOldType] = useState<ShiftType>(type);

  useEffect(() => {
    const newCalendarWeek = getCalendarWeek(new Date(date));

    if (newCalendarWeek !== calendarWeek || oldType !== type) {
      loadWeek(newCalendarWeek);
      setOldType(type);
    }
  }, [restaurantId, type, date, view]);

  const onShiftAction = (
    id: string,
    dateId: string,
    actionType: ShiftActionType,
    payload?: any,
    grant?: SecurityGrant
  ) => {
    const date = dates.find((d) => d.date === dateId);

    const shift = date?.shifts.find((s) => s.id === id) ?? payload?.shift;

    let shiftType = shift?.type ?? type;

    const subCollection =
      shiftType === ShiftType.RESERVATION
        ? 'reservationDates'
        : shiftType === ShiftType.GUEST_ORDERING
        ? 'guestOrderingDates'
        : shiftType === ShiftType.HAPPY_HOURS
        ? 'happyHourDates'
        : shiftType === ShiftType.TAKEAWAY
        ? 'takeAwayDates'
        : '';

    // console.log({ actionType, shift, subCollection, shiftType });

    if (!subCollection) return;

    const ref = db
      .collection(`restaurants/${restaurantId}/${subCollection}`)
      .doc(dateId);

    if (!shift && actionType !== ShiftActionType.BRING_BACK_FROM_DELETED) {
      newToast('An Error occured', Severity.ERROR, 'errors');
      return;
    }

    const shiftTargets = {
      targets: [
        { type: 'shift' as const, id: shift?.id ?? id },
        { type: 'day' as const, id: dateId },
      ],
    };

    // The PIN that opened an editor also covers its save (saveActiveShift).
    const openEditor = (editor: NonNullable<ActiveShift<TShift>>) =>
      setactiveShift({ ...editor, grants: grant ? [grant] : [] });

    const patchShift = (
      patch: (stored: any) => any,
      successMessage: string,
      action = 'shift.change'
    ) =>
      trackChange(grant, action, updateShiftOnDay(ref, shift.id, patch), shiftTargets)
        .then((patched) => {
          setdates((dates) =>
            dates.map((d) =>
              d.date === dateId
                ? ({
                    ...d,
                    shifts: d.shifts.map((s) =>
                      s.id === shift.id ? patched : s
                    ),
                  } as any)
                : d
            )
          );
          newToast(successMessage, Severity.SUCCESS);
        })
        .catch((error) => {
          newToast(error.message, Severity.ERROR, 'errors');
        });

    switch (actionType) {
      case ShiftActionType.CLOSE_RESERVATIONS: {
        const s = shift as unknown as ReservationShift;

        if (
          (s.type === ShiftType.RESERVATION &&
            s.overwrite?.blockReservations) ||
          ((shift.type === ShiftType.GUEST_ORDERING ||
            shift.type === ShiftType.HAPPY_HOURS) &&
            s.closed)
        ) {
          newToast('Shift is allready Blocked', Severity.ERROR, 'errors');
          return;
        }

        if (shift.type === ShiftType.RESERVATION) {
          patchShift(
            (stored) => ({
              ...stored,
              overwrite: { ...stored.overwrite, blockReservations: true },
            }),
            'Shift was blocked for further Reservations',
            'shift.closeReservations'
          );
        } else if (
          shift.type === ShiftType.GUEST_ORDERING ||
          shift.type === ShiftType.HAPPY_HOURS
        ) {
          patchShift(
            (stored) => ({ ...stored, closed: true }),
            'Shift was blocked for further Reservations',
            'shift.closeReservations'
          );
        }

        return;
      }
      case ShiftActionType.OPEN_RESERVATIONS: {
        const s = shift as unknown as ReservationShift;

        if (
          (shift.type === ShiftType.RESERVATION &&
            !s.overwrite?.blockReservations) ||
          ((shift.type === ShiftType.GUEST_ORDERING ||
            shift.type === ShiftType.HAPPY_HOURS) &&
            !s.closed)
        ) {
          newToast('Shift is not blocked', Severity.ERROR, 'errors');
          return;
        }

        if (shift.type === ShiftType.RESERVATION) {
          patchShift(
            (stored) => ({
              ...stored,
              overwrite: { ...stored.overwrite, blockReservations: false },
            }),
            'Shift was successfully unblocked',
            'shift.openReservations'
          );
        } else if (
          shift.type === ShiftType.GUEST_ORDERING ||
          shift.type === ShiftType.HAPPY_HOURS
        ) {
          patchShift(
            (stored) => ({ ...stored, closed: false }),
            'Shift was successfully unblocked',
            'shift.openReservations'
          );
        }

        return;
      }
      case ShiftActionType.CLOSE_TERRACE: {
        const s = shift as unknown as ReservationShift;

        if (shift.type !== ShiftType.RESERVATION) return;

        if (
          s.overwrite?.placeAt === 'indoor' ||
          (s.overwrite?.placeAt === undefined &&
            s.additional?.placeAt === 'indoor')
        ) {
          newToast(
            'Seating is allready only allowed indoors',
            Severity.ERROR,
            'errors'
          );
          return;
        }

        //@TODO Ask if they want to move all Reservations indoor;
        patchShift(
          (stored) => ({
            ...stored,
            overwrite: { ...stored.overwrite, placeAt: 'indoor' },
          }),
          'Terrace was successfully closed'
        );

        return;
      }
      case ShiftActionType.OPEN_TERRACE: {
        if (shift.type !== ShiftType.RESERVATION) return;

        const s = shift as unknown as ReservationShift;

        if (
          s.overwrite?.placeAt !== 'indoor' &&
          s.overwrite?.placeAt === undefined &&
          s.additional?.placeAt !== 'indoor'
        ) {
          newToast(
            'Terrace is allready open for Reservations',
            Severity.ERROR,
            'errors'
          );
          return;
        }

        //@TODO Ask if they want to move all Reservations outdoor;
        patchShift(
          (stored) => ({
            ...stored,
            overwrite: { ...stored.overwrite, placeAt: null },
          }),
          'Terrace was successfully opened'
        );

        return;
      }
      case ShiftActionType.EDIT_SHIFT: {
        if (shift.regular && !shift.edited) {
          alert({
            title: 'Edit all upcoming Shifts?',
            maxWidth: 'sm',
            fullWidth: true,
            description:
              'Choose between editing all upcoming shifts or only on the selected dates',
            actions: [
              {
                label: 'No, only on this day',
                onClick: () => {
                  setDate(dateId);
                  openEditor({
                    shift,
                    date: dateId,
                    type: 'edit',
                    allUpcomingShifts: false,
                  });
                },
                id: 'onlyThisShift',
              },
              {
                label: 'Yes, on all future dates',
                variant: 'primary',
                id: 'allUpcomingShifts',
                onClick: () => {
                  setDate(dateId);
                  openEditor({
                    shift: {
                      ...shift,
                      startAt: dateId,
                      dates:
                        shift?.dates?.filter((d: any) => d < dateId) ??
                        undefined,
                    },
                    date: dateId,
                    type: 'edit',
                    allUpcomingShifts: true,
                  });
                },
              },
            ],
            onSubmit: () => {},
          });
        } else {
          setDate(dateId);
          openEditor({
            shift,
            date: dateId,
            type: 'edit',
          });
        }
        return;
      }
      case ShiftActionType.DELETE_SHIFT: {
        alert({
          title: 'Delete Shift completly',
          description: 'Do you want to delete this shift?',
          secondaryAction: shift.regular
            ? {
                label: 'Delete all upcoming Shifts',
              }
            : undefined,
          onSubmit: (allFutureDates: boolean = false) => {
            setloading(true);

            trackChange(
              grant,
              'shift.delete',
              server.post<{ success: boolean }>(
                `/v03/shifts/${restaurantId}/shift/${shift.id}/${shift.type}`,
                {
                  date: dateId,
                  allFutureDates,
                  type: actionType,
                  disableOnly: !shift.disabled && !shift.commentOnly,
                  uid,
                } as ShiftActionBody
              ),
              shiftTargets
            )
              .then(() => {
                loadWeek();
                newToast('Shift was successfully deleted', Severity.SUCCESS);
                setcurrentShift(null);
              })
              .catch((err) => {
                newToast(
                  err?.response?.data?.message ?? err.message,
                  Severity.ERROR,
                  'errors'
                );
              })
              .finally(() => {
                triggerCustomEvent('triggerShiftReload');
                setloading(false);
              });
          },
        });

        return;
      }
      case ShiftActionType.RESTORE_SHIFT: {
        setloading(true);

        trackChange(
          grant,
          'shift.restore',
          server.post<{ success: boolean }>(
            `/v03/shifts/${restaurantId}/shift/${shift.id}/${shift.type}`,
            {
              date: dateId,
              allFutureDates: true,
              type: actionType,
              uid,
            } as ShiftActionBody
          ),
          shiftTargets
        )
          .then(() => {
            loadWeek();
            newToast('Shift was successfully restored', Severity.SUCCESS);
            setcurrentShift(null);
          })
          .catch((err) => {
            newToast(
              err?.response?.data?.message ?? err.message,
              Severity.ERROR,
              'errors'
            );
          })
          .finally(() => setloading(false));

        return;
      }
      case ShiftActionType.DUPLICATE_SHIFT: {
        openEditor({
          shift: cloneShiftEventWithNewId({
            ...shift,
            name: `${shift.name} Kopie`,
            id: 'newId',
            startAt: dateId,
            // A copy is a new rule: no day state, and no replacesShift (it would hide the original series).
            replacesShift: undefined,
            disabled: false,
            edited: undefined,
          }),
          date: dateId,
          type: 'duplication',
          allUpcomingShifts: true,
        });

        return;
      }
      case ShiftActionType.ADD_EVENT: {
        const event = payload;

        let newShift = { ...shift };

        if (event && (newShift as any).additional) {
          newShift = {
            ...newShift,
            additional: {
              ...newShift.additional,
              eventV02: event,
            },
          };
        }

        if (shift.regular) {
          alert({
            title: 'Edit all upcoming Events of this Shift?',
            maxWidth: 'sm',
            fullWidth: true,
            description:
              'Choose between editing all upcoming shifts or only on the selected dates',
            actions: [
              {
                label: 'No, only on this day',
                onClick: () => {
                  setDate(dateId);
                  openEditor({
                    shift: newShift,
                    date: dateId,
                    type: 'edit',
                    allUpcomingShifts: false,
                    eventOnly: true,
                  });
                },
                id: 'onlyThisShift',
              },
              {
                label: 'Yes, on all future dates',
                variant: 'primary',
                id: 'allUpcomingShifts',
                onClick: () => {
                  setDate(dateId);
                  openEditor({
                    shift: newShift,
                    date: dateId,
                    type: 'edit',
                    allUpcomingShifts: true,
                    eventOnly: true,
                  });
                },
              },
            ],
            onSubmit: () => {},
          });
        } else {
          setDate(dateId);
          openEditor({
            shift: newShift,
            date: dateId,
            type: 'edit',
            eventOnly: true,
          });
        }
        return;
      }
      case ShiftActionType.EDIT_EVENT: {
        if (shift.regular) {
          // @ts-ignore
          if ((shift as ReservationShift)?.additional?.eventV02?.edited) {
            openEditor({
              shift,
              date: dateId,
              type: 'edit',
              eventOnly: true,
              allUpcomingShifts: false,
            });
          } else {
            alert({
              title: 'Edit all upcoming Events of this Shift?',
              maxWidth: 'sm',
              fullWidth: true,
              description:
                'Choose between editing all upcoming shifts or only on the selected dates',
              actions: [
                {
                  label: 'No, only on this day',
                  onClick: () => {
                    setDate(dateId);
                    openEditor({
                      shift,
                      date: dateId,
                      type: 'edit',
                      allUpcomingShifts: false,
                      eventOnly: true,
                    });
                  },
                  id: 'onlyThisShift',
                },
                {
                  label: 'Yes, on all future dates',
                  variant: 'primary',
                  id: 'allUpcomingShifts',
                  onClick: () => {
                    setDate(dateId);
                    openEditor({
                      shift,
                      date: dateId,
                      type: 'edit',
                      allUpcomingShifts: true,
                      eventOnly: true,
                    });
                  },
                },
              ],
              onSubmit: () => {},
            });
          }
        } else {
          setDate(dateId);
          openEditor({
            shift,
            date: dateId,
            type: 'edit',
            eventOnly: true,
          });
        }
        return;
      }
      case ShiftActionType.BRING_BACK_FROM_DELETED: {
        // console.log({ payload });
        // if (payload.shift) {
        //   let { shift } = payload;

        //   setDate(dateId);
        //   setactiveShift({
        //     shift,
        //     date: dateId,
        //     type: "edit",
        //     allUpcomingShifts: false,
        //   });
        // }

        //  console.log(`TESTING`);

        server
          .get(
            `/v03/shifts/${restaurantId}/shift/${id}/${type}/${dateId}/bringBackFromDeleted`
          )
          .then(() => {
            loadWeek();
            newToast('Shift was successfully restored', Severity.SUCCESS);
          })
          .catch((error) => {
            // a 404 means the restore entry is gone on the server, drop it here too
            if (error?.response?.status === 404) {
              setdates((dates) =>
                dates.map((d) =>
                  d.date === dateId
                    ? {
                        ...d,
                        deleted: d.deleted?.filter((shiftId) => shiftId !== id),
                        deletedV02: d.deletedV02?.filter((entry) => entry.id !== id),
                      }
                    : d
                )
              );
            }
            newToast(
              error?.response?.data?.message ?? error.message,
              Severity.ERROR,
              'errors'
            );
          });

        return;
      }
      default:
        newToast('An error occurred', Severity.ERROR, 'errors');
        return;
    }
  };

  const [newShiftSidebar, setnewShiftSidebar] = useState<null | string>(null);

  const handleNewShift = (
    dateId: string = date,
    from?: string,
    till?: string
  ) => {
    if (!from) {
      setnewShiftSidebar(dateId);
    } else {
      newShiftFromTemplate('new', 'noTemplate', dateId, from, till);
    }
    setcurrentShift(null);
    setCurrentMultipleShifts(null);
    setSelectedMultiCard(null);
  };

  // The PIN that opened the new-shift dialog covers the shift it then creates.
  const newShiftGrant = useRef<SecurityGrant | undefined>(undefined);

  const closeNewShift = () => {
    setnewShiftSidebar(null);
    newShiftGrant.current = undefined;
  };
  const rememberNewShiftGrant = (grant?: SecurityGrant) => {
    newShiftGrant.current = grant;
  };

  // const recentShifts = useMemo(
  //   () =>
  //     dates.reduce((acc, cV) => {
  //       cV.shifts.forEach((s) => {
  //         if (s.regular && !acc.find((s1) => s.id === s1.id)) {
  //           acc.push(s as AnyShift);
  //         }
  //       });
  //       return acc;
  //     }, [] as AnyShift[]),
  //   [dates]
  // );

  const { recentShifts } = useRecentShifts(restaurantId, type, dates);

  const newShiftFromTemplate = (
    id: string,
    action: 'fromTemplate' | 'noTemplate' | 'fromRecent' | 'commentTemplate',
    dateId?: string,
    from?: string,
    till?: string
  ) => {
    // if (!newShiftSidebar) return;

    let startAt: string | null = null;

    if (!dateId) {
      let dObj = new Date(date);
      let wDay = dObj.getDay();

      if (wDay !== 1) {
        startAt = dateHelper(
          new Date(dObj.getTime() - (wDay - 1) * 24 * 60000 * 60)
        );
      }

      if (date < dateHelper()) {
        startAt = dateHelper();
      }
    }

    dateId = dateId || startAt || newShiftSidebar || date;

    if (!startAt) {
      startAt = dateId;
    }

    if (dateId < dateHelper()) {
      dateId = dateHelper();
    }

    let colors = dates.reduce((acc: string[], cV) => {
      return acc.filter(
        (c) => !cV.shifts.find((s) => s.regular && s.color === c)
      );
    }, COLORS);

    let color = colors?.[0] ?? '#10CCB9';

    let weekDays = [new Date(dateId).getDay()];

    const openNewEditor = (editor: NonNullable<ActiveShift<TShift>>) => {
      const grant = newShiftGrant.current;
      newShiftGrant.current = undefined;
      setactiveShift({ ...editor, grants: grant ? [grant] : [] });
    };

    // load occassions

    switch (action) {
      case 'noTemplate': {
        let start = timeToBlock(from ?? '18:00');
        let close = timeToBlock(till ?? '22:00');
        let service = close - 8;

        if (close < start) {
          close += 96;
        }

        if (service < start) {
          service = close;
        }

        console.log({ start, close, service, from, till });

        const occasions = allOccassions.map((o) => o.id);

        let newShift: Partial<AnyShift> = {
          id: 'new-shift',
          name: '',
          color,
          active: true,
          regular: true,
          priority: 0,
          weekDays,
          startAt,
          start,
          service,
          close,
          type: type as
            | ShiftType.RESERVATION
            | ShiftType.GUEST_ORDERING
            | ShiftType.HAPPY_HOURS
            | undefined,
        };

        if (newShift.type === ShiftType.RESERVATION) {
          newShift.additional = {
            blockedAt: [],
            slotsPerBlock: {
              rules: [],
              standard: 20,
            },
            reservationLength: {
              rules: [],
              standard: 8,
            },
            floorPlan: null,
            placeAt: null,
          };
          newShift.spaces = [];
          newShift.occassions = allOccassions.map((o) => o.id);
        }

        if (newShift.type === ShiftType.GUEST_ORDERING) {
          (newShift as GuestOrderingShift).menu = null;
        }

        if (newShift.type === ShiftType.TAKEAWAY) {
          (newShift as TakeAwayShift).menu = null;
        }

        openNewEditor({
          date: dateId,
          shift: newShift as Partial<TShift>,
          type: 'new',
          allUpcomingShifts: true,
        });
        setDate(dateId);
        setCurrentMultipleShifts(null);
        setSelectedMultiCard(null);
        setcurrentShift(null);

        break;
      }
      case 'commentTemplate': {
        let newShift: ReservationShift = {
          id: 'new-shift',
          name: '',
          color,
          regular: false,
          priority: 0,
          weekDays: [],
          dates: [dateId],
          endAt: dateId,
          startAt: dateId,
          start: 72,
          service: 92,
          close: 96,
          additional: {
            blockedAt: [],
            slotsPerBlock: {
              rules: [],
              standard: 20,
            },
            reservationLength: {
              rules: [],
              standard: 8,
            },
            floorPlan: null,
            placeAt: null,
          },
          spaces: [],
          occassions: [],
          active: true,
          createdAt: Date.now(),
          commentOnly: true,
          createdBy: uid ?? '',
          type: ShiftType.RESERVATION,
        };

        openNewEditor({
          date: dateId,
          shift: newShift as unknown as Partial<TShift>,
          type: 'new',
          allUpcomingShifts: true,
        });
        setDate(dateId);
        setCurrentMultipleShifts(null);
        setSelectedMultiCard(null);
        setcurrentShift(null);

        break;
      }
      case 'fromRecent': {
        const recent = recentShifts.find((r) => r.id === id) as TShift;

        if (!recent) return;

        let newShift: Partial<TShift> & { overwrite?: any; occassions?: any } =
          {
            ...recent,
            id: 'new-shift',
            name: '',
            startAt: dateId,
            color,
            disabled: false,
            replacesShift: undefined,
            edited: undefined,
          };

        if (newShift.type === ShiftType.RESERVATION) {
          newShift = cloneShiftEventWithNewId({
            ...(newShift as Partial<ReservationShift>),
            overwrite: {},
            occassions: allOccassions.map((o) => o.id),
          }) as Partial<TShift> & { overwrite?: any; occassions?: any };
        }

        openNewEditor({
          date: dateId,
          shift: newShift,
          type: 'new',
          allUpcomingShifts: true,
        });
        setDate(dateId);
        setCurrentMultipleShifts(null);
        setSelectedMultiCard(null);
        setcurrentShift(null);

        break;
      }
      case 'fromTemplate': {
        const template = TEMPLATES[id];

        const occassions = allOccassions.map((o) => o.id);

        if (id === 'template-3') {
          template.dates = getDateArray(dateId, 7);
          template.endAt = template.dates[template.dates.length - 1];
        } else if (!template.regular) {
          template.dates = [dateId];
        }

        let newShift: Partial<AnyShift> = {
          ...template,
          id: 'new-shift',
          startAt: dateId,
          color,
        };

        if (newShift.type === ShiftType.RESERVATION) {
          newShift = cloneShiftEventWithNewId({
            ...(newShift as Partial<ReservationShift>),
            overwrite: {},
            occassions,
          }) as Partial<AnyShift>;
        }

        openNewEditor({
          date: dateId,
          shift: newShift as unknown as Partial<TShift>,
          type: 'new',
          allUpcomingShifts: true,
        });
        setDate(dateId);
        setCurrentMultipleShifts(null);
        setSelectedMultiCard(null);
        setcurrentShift(null);

        break;
      }
      default:
        break;
    }

    setnewShiftSidebar(null);
  };

  const endAt = useDateString(activeShift?.shift?.endAt ?? '');

  const saveActiveShift = async (
    activeShiftOverride?: ActiveShift<TShift>
  ) => {
    const activeShiftToSave = activeShiftOverride ?? activeShift;
    if (!activeShiftToSave) return;

    const shift = activeShiftToSave.shift;
    const original = dates.find(day => day.date === activeShiftToSave.date)?.shifts.find(before => before.id === shift.id);
    const overrideChanges = shift.type === ShiftType.RESERVATION && activeShiftToSave.type === 'edit' && !activeShiftToSave.allUpcomingShifts && original
      ? dayOverrideChanges(original as ReservationShift, shift as ReservationShift)
      : undefined;

    triggerCustomEvent('triggerShiftReload');

    // Each Security Plus grant that opened or changed this editor sees the
    // outcome of the save it led to.
    const saveAction =
      activeShiftToSave.type === 'edit'
        ? activeShiftToSave.eventOnly
          ? 'shift.eventSave'
          : 'shift.save'
        : activeShiftToSave.type === 'duplication'
        ? 'shift.duplicateSave'
        : 'shift.create';
    // An edited shift has its id; a new shift or copy only carries a placeholder
    // until the server answers with the id it created.
    const day = { type: 'day' as const, id: activeShiftToSave.date };
    const savedShift =
      activeShiftToSave.type === 'edit' && shift.id && !PLACEHOLDER_SHIFT_IDS.includes(shift.id)
        ? [{ type: 'shift' as const, id: shift.id }]
        : [];
    const tracked = (write: Promise<AxiosResponse<ShiftSaveAnswer>>) =>
      (activeShiftToSave.grants ?? []).reduce(
        (settled, grant) =>
          trackChange(grant, saveAction, settled, {
            targets: [...savedShift, day],
            fromResult: (answer) =>
              answer?.data?.shiftId
                ? { targets: [{ type: 'shift', id: answer.data.shiftId }, day] }
                : {},
          }),
        write
      );
    const post = (url: string, body: ShiftActionBody) =>
      tracked(server.post<ShiftSaveAnswer>(url, body));

    switch (activeShiftToSave.type) {
      case 'edit': {
        let shiftBefore =
          dates
            .find((d) => d.date === currentShift?.date)
            ?.shifts?.find((s) => s.id === currentShift?.shiftId) ?? null;

        // Send the block only when the editor changed it, a stale week must not undo a block set elsewhere
        const singleDayData =
          activeShiftToSave.blockBefore !== undefined &&
          !!(shift as any).overwrite?.blockReservations !==
            activeShiftToSave.blockBefore
          ? shift
          : {
              ...shift,
              overwrite: {
                ...(shift as any).overwrite,
                blockReservations: undefined,
              },
            };

        let wDaysChanged =
          !shiftBefore ||
          (shiftBefore.weekDays?.length ?? 0) !==
            (shift.weekDays?.length ?? 0) ||
          !!(shiftBefore.weekDays ?? []).find(
            (s) => !shift.weekDays?.includes(s)
          );

        let endAtChanged = !!shiftBefore && shiftBefore.endAt !== shift.endAt;

        let endAtSmaller =
          !!shiftBefore &&
          (!shiftBefore.endAt || (shift.endAt ?? '') < shiftBefore.endAt);

        if (endAtChanged && endAtSmaller && shift.endAt) {
          let title =
            language === 'de'
              ? `Alle Schichten nach dem ${endAt} löschen?`
              : `Do you want to delete all shifts after ${endAt}?`;
          let description =
            language === 'de'
              ? 'Wähle zwischen dem löschen aller Schichten nach dem neuen Enddatum und dem behalten mit alten Einstellungen'
              : 'Choose between deleting shifts that are further in the future and falling back to the old settings.';

          alert({
            title,
            maxWidth: 'sm',
            fullWidth: true,
            description,
            actions: [
              {
                label: 'No, keep the future shifts',
                onClick: () => {
                  setloading(true);
                  post(
                      `/v03/shifts/${restaurantId}/shift/${shift.id}/${shift.type}`,
                      {
                        date: activeShiftToSave.date,
                        allFutureDates: true,
                        deleteFuture: false,
                        type: ShiftActionType.EDIT_SHIFT,
                        data: shift,
                        uid,
                      } as ShiftActionBody
                    )
                    .then(() => {
                      loadWeek();
                      newToast(
                        'Shift was successfully edited',
                        Severity.SUCCESS
                      );
                    })
                    .catch((err) => {
                      newToast(
                        err?.response?.data?.message ?? err.message,
                        Severity.ERROR,
                        'errors'
                      );
                    })
                    .finally(() => setloading(false));
                },
                id: 'onlyThisShift',
              },
              {
                label: 'Yes, delete future shifts',
                variant: 'primary',
                id: 'onlyThisShift',
                onClick: () => {
                  setloading(true);
                  post(
                      `/v03/shifts/${restaurantId}/shift/${shift.id}/${shift.type}`,
                      {
                        date: activeShiftToSave.date,
                        allFutureDates: true,
                        deleteFuture: true,
                        type: ShiftActionType.EDIT_SHIFT,
                        data: shift,
                        uid,
                      } as ShiftActionBody
                    )
                    .then(() => {
                      loadWeek();
                      newToast(
                        'Shift was successfully edited',
                        Severity.SUCCESS
                      );
                    })
                    .catch((err) => {
                      newToast(
                        err?.response?.data?.message ?? err.message,
                        Severity.ERROR,
                        'errors'
                      );
                    })
                    .finally(() => setloading(false));
                },
              },
            ],
            onSubmit: () => {},
          });
        } else if (
          (shift.regular && !wDaysChanged && !endAtChanged) ||
          (shift.dates &&
            shift.dates.filter((d) => d > activeShiftToSave.date).length)
        ) {
          if (!activeShiftToSave.allUpcomingShifts) {
            setloading(true);
            post(
                `/v03/shifts/${restaurantId}/shift/${shift.id}/${shift.type}`,
                {
                  date: activeShiftToSave.date,
                  allFutureDates: false,
                  type: ShiftActionType.EDIT_SHIFT,
                  data: singleDayData,
                  dayOverrideChanges: overrideChanges,
                  uid,
                } as ShiftActionBody
              )
              .then(() => {
                loadWeek();
                newToast('Shift was successfully edited', Severity.SUCCESS);
              })
              .catch((err) => {
                newToast(
                  err?.response?.data?.message ?? err.message,
                  Severity.ERROR,
                  'errors'
                );
              })
              .finally(() => setloading(false));
          } else if (activeShiftToSave.allUpcomingShifts) {
            setloading(true);
            post(
                `/v03/shifts/${restaurantId}/shift/${shift.id}/${shift.type}`,
                {
                  date: activeShiftToSave.date,
                  allFutureDates: true,
                  type: ShiftActionType.EDIT_SHIFT,
                  data: shift,
                  uid,
                } as ShiftActionBody
              )
              .then(() => {
                loadWeek();
                newToast('Shift was successfully edited', Severity.SUCCESS);
              })
              .catch((err) => {
                newToast(
                  err?.response?.data?.message ?? err.message,
                  Severity.ERROR,
                  'errors'
                );
              })
              .finally(() => setloading(false));
          }
        }
        // alert({
        //   title: "Edit all upcoming Shifts?",
        //   maxWidth: "sm",
        //   fullWidth: true,
        //   description:
        //     "Choose between editing all upcoming shifts or only on the selected dates",
        //   actions: [
        //     {
        //       label: "No, only on this day",
        //       onClick: () => {
        //         setloading(true);
        //         server
        //           .post<{ success: boolean }>(
        //             `/v03/shifts/${restaurantId}/shift/${shift.id}/${shift.type}`,
        //             {
        //               date: activeShift.date,
        //               allFutureDates: false,
        //               type: ShiftActionType.EDIT_SHIFT,
        //               data: shift,
        //               uid,
        //             } as ShiftActionBody
        //           )
        //           .then(() => {
        //             loadWeek();
        //             newToast("Shift was successfully edited", Severity.SUCCESS);
        //           })
        //           .catch((err) => {
        //             newToast(
        //               err?.response?.data?.message ?? err.message,
        //               Severity.ERROR,
        //               "errors"
        //             );
        //           })
        //           .finally(() => setloading(false));
        //       },
        //       id: "onlyThisShift",
        //     },
        //     {
        //       label: "Yes, on all future dates",
        //       variant: "primary",
        //       id: "onlyThisShift",
        //       onClick: () => {
        //         setloading(true);
        //         server
        //           .post<{ success: boolean }>(
        //             `/v03/shifts/${restaurantId}/shift/${shift.id}/${shift.type}`,
        //             {
        //               date: activeShift.date,
        //               allFutureDates: true,
        //               type: ShiftActionType.EDIT_SHIFT,
        //               data: shift,
        //               uid,
        //             } as ShiftActionBody
        //           )
        //           .then(() => {
        //             loadWeek();
        //             newToast("Shift was successfully edited", Severity.SUCCESS);
        //           })
        //           .catch((err) => {
        //             newToast(
        //               err?.response?.data?.message ?? err.message,
        //               Severity.ERROR,
        //               "errors"
        //             );
        //           })
        //           .finally(() => setloading(false));
        //       },
        //     },
        //   ],
        //   onSubmit: () => {},
        // });
        else {
          setloading(true);

          let oldDate = activeShiftToSave.date;

          let date = !activeShiftToSave.shift.regular
            ? activeShiftToSave.shift.dates?.[0] ?? activeShiftToSave.date
            : activeShiftToSave.date;

          // Same target day as editShift in the api, a moved shift is pushed as sent and needs its block
          let movedTo =
            !shift.regular && shift.startAt !== date
              ? shift.startAt ?? date
              : date;

          post(
              `/v03/shifts/${restaurantId}/shift/${shift.id}/${shift.type}`,
              {
                date,
                oldDate,
                allFutureDates: activeShiftToSave.allUpcomingShifts,
                type: ShiftActionType.EDIT_SHIFT,
                deleteFuture: false,
                data: movedTo === oldDate ? singleDayData : shift,
                dayOverrideChanges: overrideChanges,
                uid,
              } as ShiftActionBody
            )
            .then(() => {
              loadWeek();
              newToast('Shift was successfully edited', Severity.SUCCESS);
            })
            .catch((err) => {
              newToast(
                err?.response?.data?.message ?? err.message,
                Severity.ERROR,
                'errors'
              );
            })
            .finally(() => {
              setloading(false);
              triggerCustomEvent('triggerShiftReload');
            });
        }

        break;
      }
      case 'new': {
        const data =
          shift.type === ShiftType.RESERVATION
            ? new NewReservationShift(shift as any).toData()
            : shift.type === ShiftType.GUEST_ORDERING
            ? new NewGuestOrderingShift(shift as any).toData()
            : shift.type === ShiftType.HAPPY_HOURS
            ? new NewHappyHoursShift(shift as any).toData()
            : shift.type === ShiftType.TAKEAWAY
            ? new NewHappyHoursShift(shift as any).toData()
            : {};

        if (shift.regular && shift.weekDays && shift.weekDays.length === 0) {
          alert({
            title: 'Keine Wochentage ausgewählt',
            description:
              'Bitte wähle mindestens einen Wochentag aus um fortzufahren',
            onSubmit: () => {},
            actions: [
              {
                id: 'ok',
                label: 'Ok',
                variant: 'primary',
              },
            ],
          });
          return;
        }

        setloading(true);

        post(
            `/v03/shifts/${restaurantId}/shift/new/${shift.type}`,
            {
              date: shift.startAt,
              type: ShiftActionType.NEW_SHIFT,
              data,
              uid,
            } as ShiftActionBody
          )
          .then(() => {
            loadWeek();
            newToast('Shift was successfully created', Severity.SUCCESS);
          })
          .catch((err) => {
            newToast(
              err?.response?.data?.message ?? err.message,
              Severity.ERROR,
              'errors'
            );
          })
          .finally(() => {
            setloading(false);
            triggerCustomEvent('triggerShiftReload');
          });
        break;
      }
      case 'duplication': {
        // @TODO Clean up data
        if (shift.type !== ShiftType.RESERVATION) return;
        const data = new NewReservationShift(
          shift as unknown as ReservationShift
        ).toData();

        setloading(true);

        post(
            `/v03/shifts/${restaurantId}/shift/new/${shift.type}`,
            {
              date: shift.startAt || activeShiftToSave.date,
              type: ShiftActionType.NEW_SHIFT,
              data,
              uid,
            } as ShiftActionBody
          )
          .then(() => {
            loadWeek();
            newToast('Shift was successfully duplicated', Severity.SUCCESS);
          })
          .catch((err) => {
            newToast(
              err?.response?.data?.message ?? err.message,
              Severity.ERROR,
              'errors'
            );
          })
          .finally(() => {
            setloading(false);
            triggerCustomEvent('triggerShiftReload');
          });
        break;
      }
      default: {
        newToast('An Error occured', Severity.ERROR, 'errors');
        break;
      }
    }

    setactiveShift(null);
  };

  const datesWithActiveShifts = useMemo(() => {
    return !activeShift
      ? dates
      : dates.map((d) => {
          const shift = activeShift.shift;

          let wDay = new Date(d.date).getDay();

          let isRegularShift =
            shift.regular &&
            shift.startAt &&
            shift.startAt <= d.date &&
            (!shift.endAt || shift.endAt >= d.date) &&
            shift.weekDays &&
            shift.weekDays.includes(wDay);

          let isSpecialShift =
            !shift.regular && shift.startAt && shift.startAt === d.date;

          let isOnThisDay =
            (isRegularShift || isSpecialShift) &&
            checkActiveShift(activeShift, activeShift?.shift?.id, d.date);

          if (!isOnThisDay) {
            return d;
          }

          const shifts = Array.from([...d.shifts]);

          let i = shifts.findIndex((s) =>
            checkActiveShift(activeShift, s.id, d.date)
          );

          if (i === -1) {
            shifts.push(shift as any);
          } else {
            shifts[i] = shift as any;
          }

          return {
            ...d,
            shifts,
          };
        });
  }, [dates, activeShift]);

  return {
    dates: datesWithActiveShifts,
    calendarWeek,
    loading,
    error,
    onShiftAction,
    activeShift,
    setactiveShift,
    handleNewShift,
    saveActiveShift,
    currentShift,
    setcurrentShift,
    closeNewShift,
    rememberNewShiftGrant,
    recentShifts,
    newShiftFromTemplate,
    newShiftSidebar,
    currentMultipleShifts,
    setCurrentMultipleShifts,
    selectedMultiCard,
    setSelectedMultiCard,
    setdates,
  };
};

export default useCalendarDates;
